import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

export default function Home({ setActivePage }) {
  const [stats, setStats] = useState({
    fmpMerchants: 0,
    goldCardMerchants: 0,
    activeCoupons: 0,
    newsItems: 0,
  });
  const [coupons, setCoupons] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);

    const [fmpMerchants, goldMerchants, couponsData, newsData] = await Promise.all([
      supabase.from('merchants').select('id', { count: 'exact' }).eq('active_fmp', true),
      supabase.from('merchants').select('id', { count: 'exact' }).eq('active_athletics', true),
      supabase.from('coupons').select('*').eq('redeemed', false).eq('active_fmp', true).order('created_at', { ascending: false }),
      supabase.from('news').select('*').order('created_at', { ascending: false }).limit(5),
    ]);

    setStats({
      fmpMerchants: fmpMerchants.count || 0,
      goldCardMerchants: goldMerchants.count || 0,
      activeCoupons: couponsData.data?.length || 0,
      newsItems: newsData.data?.length || 0,
    });

    setCoupons(couponsData.data || []);
    setNews(newsData.data || []);
    setLoading(false);
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    await supabase.from('coupons').delete().eq('id', id);
    fetchData();
  };

  const handleToggleNews = async (item, field) => {
    await supabase
      .from('news')
      .update({ [field]: !item[field] })
      .eq('id', item.id);
    fetchData();
  };

  if (loading) return (
    <div style={styles.loading}>
      <p style={{ color: GOLD }}>Loading...</p>
    </div>
  );

  return (
    <div style={styles.wrap}>

     {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>Dashboard</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={styles.topbarBtn} onClick={() => setActivePage('merchants')}>
            + Add Merchant
          </button>
          <button style={styles.topbarBtn} onClick={() => setActivePage('coupons')}>
            + Add Coupon
          </button>
          <button style={styles.topbarBtn} onClick={() => setActivePage('news')}>
            + Add News
          </button>
        </div>
      </div>

      <div style={styles.content}>

        {/* STAT CARDS */}
        <div style={styles.statGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>FMP Merchants</div>
            <div style={styles.statValue}>{stats.fmpMerchants}</div>
            <div style={styles.statSub}>Active in FMP app</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Gold Card Merchants</div>
            <div style={styles.statValue}>{stats.goldCardMerchants}</div>
            <div style={styles.statSub}>Active in Gold Card app</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Active Coupons</div>
            <div style={{ ...styles.statValue, color: GOLD }}>{stats.activeCoupons}</div>
            <div style={styles.statSub}>Available to redeem</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>News Items</div>
            <div style={styles.statValue}>{stats.newsItems}</div>
            <div style={styles.statSub}>Published</div>
          </div>
        </div>

        {/* NEWS */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>News Items</h2>
          <button style={styles.sectionBtn} onClick={() => setActivePage('news')}>
            + Add News
          </button>
        </div>
        <div style={styles.tableWrap}>
          {news.length === 0 ? (
            <div style={styles.empty}>No news items. <span style={{ color: GOLD, cursor: 'pointer' }} onClick={() => setActivePage('news')}>Add one →</span></div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Headline', 'FMP', 'Gold Card'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {news.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>{item.headline}</td>
                    <td style={styles.td}>
                      <Toggle
                        value={item.active_fmp}
                        onChange={() => handleToggleNews(item, 'active_fmp')}
                      />
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={item.active_athletics}
                        onChange={() => handleToggleNews(item, 'active_athletics')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

         {/* ACTIVE COUPONS */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Active Coupons</h2>
          <button style={styles.sectionBtn} onClick={() => setActivePage('coupons')}>
            + New Coupon
          </button>
        </div>
        <div style={styles.tableWrap}>
          {coupons.length === 0 ? (
            <div style={styles.empty}>No active coupons. <span style={{ color: GOLD, cursor: 'pointer' }} onClick={() => setActivePage('coupons')}>Add one →</span></div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Merchant', 'Deal', 'Expires', 'Actions'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td style={styles.td}>{c.merchant_name}</td>
                    <td style={styles.td}>{c.deal}</td>
                    <td style={styles.td}>{c.expiry_date || '—'}</td>
                    <td style={styles.td}>
                      <button style={styles.actionBtn} onClick={() => handleDeleteCoupon(c.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        background: value ? GOLD : '#444',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        top: '3px',
        left: value ? '19px' : '3px',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0 },
  topbarBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  content: { padding: '24px', flex: 1 },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '28px',
  },
  statCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '16px 20px',
  },
  statLabel: { color: '#888', fontSize: '12px', marginBottom: '6px' },
  statValue: { fontSize: '28px', fontWeight: '500', color: '#111', lineHeight: 1 },
  statSub: { color: '#aaa', fontSize: '11px', marginTop: '4px' },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: '500', color: '#111', margin: 0 },
  sectionBtn: {
    background: 'none',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    color: '#666',
    cursor: 'pointer',
  },
  tableWrap: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '28px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    background: '#f9f9f9',
    color: '#888',
    fontSize: '11px',
    padding: '10px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #e0e0e0',
    fontWeight: '500',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    color: '#111',
    verticalAlign: 'middle',
  },
  actionBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  },
};